import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import {
  Subject,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';

import { SchoolModel } from '@app/core/models/school.model';
import { AppContextService } from '@app/core/services/app-context.service';
import { SelectComponent } from '@app/core/ui/components/form';
import { LoadingStateEnum } from '@app/core/ui/enums';

interface SelectOption {
  label: string;
  value: number;
}

@Component({
  selector: 'mw-topbar-filter',
  standalone: true,
  imports: [SelectComponent, ReactiveFormsModule],
  templateUrl: './topbar-filter.component.html',
  styleUrl: './topbar-filter.component.scss'
})
export class TopbarFilterComponent implements OnInit, OnDestroy {
  readonly appCtx = inject(AppContextService);

  readonly form = new FormGroup({
    school: new FormControl<number | null>(null),
    year: new FormControl<number | null>(null)
  });

  protected readonly schools = toSignal(
    this.appCtx.schools$.pipe(
      map((res) => res.result || []),
      shareReplay(1)
    )
  );
  protected readonly schoolsRequestStatus = toSignal(
    this.appCtx.schools$.pipe(map((res) => res.status))
  );

  protected selectedSchool = toSignal(
    this.appCtx.selectedSchool$.pipe(
      tap((schoolId) => this.patchFormSchool(schoolId))
    )
  );
  protected selectedCycle = toSignal(
    this.appCtx.selectedCycle$.pipe(
      tap((cycleId) => this.patchFormYear(cycleId))
    )
  );

  readonly isLoading = computed(() => {
    return this.schoolsRequestStatus() === LoadingStateEnum.loading;
  });
  protected years = signal<SelectOption[]>([]);

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.setupSchoolChangeSubscription();
    this.setupYearChangeSubscription();
    this.setupYearsBasedOnSchool();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupYearsBasedOnSchool(): void {
    this.appCtx.selectedSchool$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter((selectedSchool): selectedSchool is number => !!selectedSchool),
        switchMap((selectedSchoolId) =>
          this.appCtx.schools$.pipe(
            map((schools) => ({
              selectedSchoolId,
              schools: schools.result || []
            }))
          )
        ),
        tap(({ schools }) => this.handleFormSchoolAvailability(schools)),
        map(({ selectedSchoolId, schools }) =>
          this.extractAndFormatYears(selectedSchoolId, schools)
        )
      )
      .subscribe((years) => {
        this.updateYearOptions(years);
      });
  }

  private setupSchoolChangeSubscription(): void {
    this.form
      .get('school')!
      .valueChanges.pipe(
        takeUntil(this.destroy$),
        filter((schoolId): schoolId is number => !!schoolId)
      )
      .subscribe((schoolId) => {
        this.handleChooseSchool(schoolId);
      });
  }

  private setupYearChangeSubscription(): void {
    this.form
      .get('year')!
      .valueChanges.pipe(
        takeUntil(this.destroy$),
        filter((cycleId): cycleId is number => !!cycleId)
      )
      .subscribe((cycleId) => {
        this.handleChooseYear(cycleId);
      });
  }

  private patchFormSchool(schoolId: number | null | undefined): void {
    const schoolControl = this.form.get('school');
    if (schoolId && schoolControl?.value !== schoolId) {
      schoolControl?.setValue(schoolId, { emitEvent: false });
    }
  }

  private patchFormYear(cycleId: number | null | undefined): void {
    const yearControl = this.form.get('year');
    if (cycleId && yearControl?.value !== cycleId) {
      yearControl?.setValue(cycleId, { emitEvent: false });
    }
  }

  private handleFormSchoolAvailability(schools: SchoolModel[]): void {
    const schoolControl = this.form.get('school');
    if (schools.length === 1) {
      schoolControl?.disable();
    } else {
      schoolControl?.enable();
    }
  }

  private extractAndFormatYears(
    selectedSchoolId: number,
    schools: SchoolModel[]
  ): SelectOption[] {
    const selectedSchoolData = schools.find(
      (school) => school.schoolId === selectedSchoolId
    );

    if (!selectedSchoolData) {
      return [];
    }

    return (
      selectedSchoolData.years
        ?.sort((a, b) => Number(a.year) - Number(b.year))
        .map((year) => ({
          label: `${year.year}`,
          value: year.cycleId
        })) || []
    );
  }

  private updateYearOptions(years: SelectOption[]): void {
    this.years.set(years);
    const yearControl = this.form.get('year');

    if (years.length === 1) {
      yearControl?.disable();
    } else {
      yearControl?.enable();
    }

    this.patchFormYear(this.selectedCycle());
  }

  handleChooseSchool(schoolId: number): void {
    if (schoolId === this.selectedSchool()) {
      return;
    }
    this.appCtx.setSelectedSchool(schoolId);
  }

  handleChooseYear(cycleId: number): void {
    if (cycleId === this.selectedCycle()) {
      return;
    }
    this.appCtx.setSelectedCycle(cycleId);
  }

  searchSchool(term: string, school: SchoolModel): boolean {
    return school.schoolName.toLowerCase().includes(term.toLowerCase());
  }
}
